/**
 * Static service-area catalog for the countries this app already supports
 * (see `TEAM_COUNTRY_OPTIONS`: AU, NZ, CA, UK, US).
 *
 * Hierarchy: country → region → city → service area.
 * `postalCode` is the value stored by `/system/servicer/add_postal_code`
 * and `/system/servicer/remove_postal_code`.
 */

export interface ServiceArea {
  postalCode: string;
  name: string;
}

export interface ServiceCity {
  code: string;
  name: string;
  areas: ServiceArea[];
}

export interface ServiceRegion {
  code: string;
  name: string;
  cities: ServiceCity[];
}

export interface ServiceCountry {
  code: string;
  name: string;
  regions: ServiceRegion[];
}

export interface ServiceAreaLocation {
  country: ServiceCountry;
  region: ServiceRegion;
  city: ServiceCity;
  area: ServiceArea;
}

export const SERVICE_AREA_CATALOG: ServiceCountry[] = [
  {
    code: 'AU',
    name: 'Australia',
    regions: [
      {
        code: 'NSW',
        name: 'New South Wales',
        cities: [
          {
            code: 'SYD',
            name: 'Sydney',
            areas: [
              { postalCode: '2000', name: 'Sydney CBD' },
              { postalCode: '2010', name: 'Surry Hills' },
              { postalCode: '2022', name: 'Bondi Junction' },
              { postalCode: '2026', name: 'Bondi' },
              { postalCode: '2042', name: 'Newtown' },
              { postalCode: '2060', name: 'North Sydney' },
              { postalCode: '2067', name: 'Chatswood' },
              { postalCode: '2095', name: 'Manly' },
              { postalCode: '2150', name: 'Parramatta' },
              { postalCode: '2170', name: 'Liverpool' },
            ],
          },
          {
            code: 'NEW',
            name: 'Newcastle',
            areas: [
              { postalCode: '2300', name: 'Newcastle' },
              { postalCode: '2291', name: 'Merewether' },
            ],
          },
        ],
      },
      {
        code: 'VIC',
        name: 'Victoria',
        cities: [
          {
            code: 'MEL',
            name: 'Melbourne',
            areas: [
              { postalCode: '3000', name: 'Melbourne CBD' },
              { postalCode: '3006', name: 'Southbank' },
              { postalCode: '3053', name: 'Carlton' },
              { postalCode: '3121', name: 'Richmond' },
              { postalCode: '3182', name: 'St Kilda' },
            ],
          },
          {
            code: 'GEE',
            name: 'Geelong',
            areas: [{ postalCode: '3220', name: 'Geelong' }],
          },
        ],
      },
      {
        code: 'QLD',
        name: 'Queensland',
        cities: [
          {
            code: 'BNE',
            name: 'Brisbane',
            areas: [
              { postalCode: '4000', name: 'Brisbane City' },
              { postalCode: '4006', name: 'Fortitude Valley' },
              { postalCode: '4101', name: 'South Brisbane' },
            ],
          },
          {
            code: 'OOL',
            name: 'Gold Coast',
            areas: [
              { postalCode: '4215', name: 'Southport' },
              { postalCode: '4217', name: 'Surfers Paradise' },
            ],
          },
        ],
      },
      {
        code: 'SA',
        name: 'South Australia',
        cities: [
          {
            code: 'ADL',
            name: 'Adelaide',
            areas: [
              { postalCode: '5000', name: 'Adelaide' },
              { postalCode: '5045', name: 'Glenelg' },
            ],
          },
        ],
      },
      {
        code: 'WA',
        name: 'Western Australia',
        cities: [
          {
            code: 'PER',
            name: 'Perth',
            areas: [
              { postalCode: '6000', name: 'Perth' },
              { postalCode: '6008', name: 'Subiaco' },
              { postalCode: '6160', name: 'Fremantle' },
            ],
          },
        ],
      },
      {
        code: 'TAS',
        name: 'Tasmania',
        cities: [
          {
            code: 'HBA',
            name: 'Hobart',
            areas: [{ postalCode: '7000', name: 'Hobart' }],
          },
        ],
      },
      {
        code: 'ACT',
        name: 'Australian Capital Territory',
        cities: [
          {
            code: 'CBR',
            name: 'Canberra',
            areas: [
              { postalCode: '2600', name: 'Canberra' },
              { postalCode: '2601', name: 'Civic' },
            ],
          },
        ],
      },
      {
        code: 'NT',
        name: 'Northern Territory',
        cities: [
          {
            code: 'DRW',
            name: 'Darwin',
            areas: [{ postalCode: '0800', name: 'Darwin' }],
          },
        ],
      },
    ],
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    regions: [
      {
        code: 'AUK',
        name: 'Auckland',
        cities: [
          {
            code: 'AKL',
            name: 'Auckland',
            areas: [
              { postalCode: '1010', name: 'Auckland Central' },
              { postalCode: '1011', name: 'Ponsonby' },
              { postalCode: '1023', name: 'Newmarket' },
              { postalCode: '2104', name: 'Manukau' },
            ],
          },
        ],
      },
      {
        code: 'WGN',
        name: 'Wellington',
        cities: [
          {
            code: 'WLG',
            name: 'Wellington',
            areas: [
              { postalCode: '6011', name: 'Wellington Central' },
              { postalCode: '5010', name: 'Lower Hutt' },
            ],
          },
        ],
      },
      {
        code: 'CAN',
        name: 'Canterbury',
        cities: [
          {
            code: 'CHC',
            name: 'Christchurch',
            areas: [
              { postalCode: '8011', name: 'Christchurch Central' },
              { postalCode: '8041', name: 'Riccarton' },
            ],
          },
        ],
      },
    ],
  },
  {
    code: 'CA',
    name: 'Canada',
    regions: [
      {
        code: 'ON',
        name: 'Ontario',
        cities: [
          {
            code: 'YTO',
            name: 'Toronto',
            areas: [
              { postalCode: 'M5H', name: 'Downtown Toronto' },
              { postalCode: 'M5V', name: 'Harbourfront' },
              { postalCode: 'M4W', name: 'Yorkville' },
              { postalCode: 'M6G', name: 'Christie' },
            ],
          },
          {
            code: 'YOW',
            name: 'Ottawa',
            areas: [
              { postalCode: 'K1P', name: 'Ottawa Centre' },
              { postalCode: 'K1N', name: 'ByWard Market' },
            ],
          },
        ],
      },
      {
        code: 'BC',
        name: 'British Columbia',
        cities: [
          {
            code: 'YVR',
            name: 'Vancouver',
            areas: [
              { postalCode: 'V6B', name: 'Downtown Vancouver' },
              { postalCode: 'V6E', name: 'West End' },
              { postalCode: 'V5K', name: 'Hastings-Sunrise' },
            ],
          },
        ],
      },
      {
        code: 'QC',
        name: 'Quebec',
        cities: [
          {
            code: 'YUL',
            name: 'Montreal',
            areas: [
              { postalCode: 'H2Y', name: 'Old Montreal' },
              { postalCode: 'H3A', name: 'Downtown Montreal' },
              { postalCode: 'H2X', name: 'Plateau' },
            ],
          },
        ],
      },
    ],
  },
  {
    code: 'UK',
    name: 'United Kingdom',
    regions: [
      {
        code: 'ENG',
        name: 'England',
        cities: [
          {
            code: 'LON',
            name: 'London',
            areas: [
              { postalCode: 'SW1A', name: 'Westminster' },
              { postalCode: 'EC1A', name: 'City of London' },
              { postalCode: 'W1', name: 'West End' },
              { postalCode: 'E1', name: 'Whitechapel' },
              { postalCode: 'N1', name: 'Islington' },
              { postalCode: 'SE1', name: 'Southwark' },
            ],
          },
          {
            code: 'MAN',
            name: 'Manchester',
            areas: [
              { postalCode: 'M1', name: 'Manchester Central' },
              { postalCode: 'M4', name: 'Ancoats' },
            ],
          },
        ],
      },
      {
        code: 'SCT',
        name: 'Scotland',
        cities: [
          {
            code: 'EDI',
            name: 'Edinburgh',
            areas: [{ postalCode: 'EH1', name: 'Edinburgh Old Town' }],
          },
          {
            code: 'GLA',
            name: 'Glasgow',
            areas: [{ postalCode: 'G1', name: 'Glasgow Centre' }],
          },
        ],
      },
    ],
  },
  {
    code: 'US',
    name: 'United States',
    regions: [
      {
        code: 'NY',
        name: 'New York',
        cities: [
          {
            code: 'NYC',
            name: 'New York',
            areas: [
              { postalCode: '10001', name: 'Chelsea' },
              { postalCode: '10011', name: 'Greenwich Village' },
              { postalCode: '11201', name: 'Brooklyn Heights' },
            ],
          },
        ],
      },
      {
        code: 'CA-US',
        name: 'California',
        cities: [
          {
            code: 'LAX',
            name: 'Los Angeles',
            areas: [
              { postalCode: '90012', name: 'Downtown Los Angeles' },
              { postalCode: '90210', name: 'Beverly Hills' },
            ],
          },
          {
            code: 'SFO',
            name: 'San Francisco',
            areas: [
              { postalCode: '94102', name: 'Civic Center' },
              { postalCode: '94111', name: 'Financial District' },
            ],
          },
        ],
      },
      {
        code: 'TX',
        name: 'Texas',
        cities: [
          {
            code: 'HOU',
            name: 'Houston',
            areas: [{ postalCode: '77002', name: 'Downtown Houston' }],
          },
          {
            code: 'AUS',
            name: 'Austin',
            areas: [{ postalCode: '78701', name: 'Downtown Austin' }],
          },
        ],
      },
      {
        code: 'IL',
        name: 'Illinois',
        cities: [
          {
            code: 'CHI',
            name: 'Chicago',
            areas: [
              { postalCode: '60601', name: 'Chicago Loop' },
              { postalCode: '60611', name: 'Streeterville' },
            ],
          },
        ],
      },
    ],
  },
];

const AREA_INDEX = new Map<string, ServiceAreaLocation>();

for (const country of SERVICE_AREA_CATALOG) {
  for (const region of country.regions) {
    for (const city of region.cities) {
      for (const area of city.areas) {
        AREA_INDEX.set(area.postalCode, { country, region, city, area });
      }
    }
  }
}

export function findServiceArea(postalCode: string): ServiceAreaLocation | undefined {
  return AREA_INDEX.get(postalCode.trim());
}

export function formatServiceAreaLabel(postalCode: string): string {
  const hit = findServiceArea(postalCode);
  if (!hit) return postalCode;
  return `${hit.area.name} (${hit.area.postalCode})`;
}

export function formatServiceAreaSummary(codes: string[]): string {
  if (codes.length === 0) return 'Add the areas you cover';
  const labels = codes.map((code) => findServiceArea(code)?.area.name ?? code);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

export function areasForCity(countryCode: string, regionCode: string, cityCode: string): ServiceArea[] {
  const country = SERVICE_AREA_CATALOG.find((item) => item.code === countryCode);
  const region = country?.regions.find((item) => item.code === regionCode);
  return region?.cities.find((item) => item.code === cityCode)?.areas ?? [];
}
